import { bitrixAjaxFetch } from './bitrix-ajax';
import { replaceLoadMoreWrap, syncListLoadMoreVisibility } from './ajax-load-more';
import { updateActivityFiltersEmptySummary } from './activity-filters-summary';
import { updateRangeEmptySummary } from './date-range-summary';
import { applyServerSearchResults, type SearchHighlightConfig } from './search-input';
import { refreshEventStatusLabels } from './event-status';
import {
  cloneFragment,
  cloneGridContent,
  fillGridWithSkeletons,
  restoreGridContent,
  type NewsListSkeletonVariant,
} from './news-list-skeleton';

export type NewsListAjaxUpdateConfig = {
  gridSelector: string;
  wrapSelector: string;
  cardSelector: string;
  loadingSelector?: string;
  skeletonVariant?: NewsListSkeletonVariant;
  skeletonCount?: number;
  modalsContainerSelector?: string;
  search?: {
    inputId?: string;
    highlight: SearchHighlightConfig;
  };
};

let activeRequest: AbortController | null = null;
const lastSuccessfulGridByKey = new WeakMap<HTMLElement, DocumentFragment>();

function setLoadMoreVisible(root: ParentNode, wrapSelector: string, visible: boolean): void {
  const wrap = root.querySelector<HTMLElement>(wrapSelector);
  if (!wrap || wrap.dataset.loadMoreExhausted === 'true') return;

  wrap.hidden = !visible;
}

export async function updateNewsListFromAjax(
  root: ParentNode,
  params: URLSearchParams,
  config: NewsListAjaxUpdateConfig,
): Promise<boolean> {
  const grid = root.querySelector<HTMLElement>(config.gridSelector);
  if (!grid) return false;

  const loadingEl = config.loadingSelector
    ? root.querySelector<HTMLElement>(config.loadingSelector)
    : grid.parentElement;

  const hasRealCards = grid.querySelector(config.cardSelector);
  if (hasRealCards) {
    lastSuccessfulGridByKey.set(grid, cloneGridContent(grid));
  }

  activeRequest?.abort();
  const abortController = new AbortController();
  activeRequest = abortController;

  const url = new URL(window.location.pathname, window.location.origin);
  params.forEach((value, key) => {
    url.searchParams.set(key, value);
  });

  if (config.skeletonVariant) {
    fillGridWithSkeletons(grid, config.skeletonVariant, config.skeletonCount);
  }

  loadingEl?.classList.add('is-loading');
  grid.setAttribute('aria-busy', 'true');
  setLoadMoreVisible(root, config.wrapSelector, false);

  try {
    const response = await bitrixAjaxFetch(url.toString(), {
      method: 'GET',
      signal: abortController.signal,
    });

    if (!response.ok) {
      throw new Error(`Filter request failed: ${response.status}`);
    }

    if (abortController.signal.aborted) {
      return false;
    }

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');

    grid.replaceChildren();
    doc.querySelectorAll(config.cardSelector).forEach((card) => {
      grid.append(card.cloneNode(true));
    });

    lastSuccessfulGridByKey.set(grid, cloneGridContent(grid));

    replaceLoadMoreWrap(root, config.wrapSelector, doc);

    if (config.modalsContainerSelector) {
      const currentModals = root.querySelector(config.modalsContainerSelector);
      const nextModals = doc.querySelector(config.modalsContainerSelector);
      if (currentModals && nextModals) {
        currentModals.replaceWith(nextModals.cloneNode(true));
      }
    }

    refreshEventStatusLabels(root);

    const queryString = params.toString();
    const nextUrl = queryString ? `${url.pathname}?${queryString}` : url.pathname;
    window.history.replaceState(null, '', nextUrl);

    return true;
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      return false;
    }

    console.error(error);

    const snapshot = lastSuccessfulGridByKey.get(grid);
    if (snapshot?.childNodes.length) {
      restoreGridContent(grid, cloneFragment(snapshot));
      setLoadMoreVisible(root, config.wrapSelector, true);
    }

    return false;
  } finally {
    if (activeRequest === abortController) {
      activeRequest = null;
    }

    if (!abortController.signal.aborted) {
      loadingEl?.classList.remove('is-loading');
      grid.removeAttribute('aria-busy');
      if (config.search) {
        const query = params.get('q') ?? params.get('search') ?? '';
        applyServerSearchResults(root, query, config.search);
      }

      syncListLoadMoreVisibility(root, {
        gridSelector: config.gridSelector,
        wrapSelector: config.wrapSelector,
        cardSelector: config.cardSelector,
      });
      updateRangeEmptySummary(root, config);
      updateActivityFiltersEmptySummary(root, config);
    }
  }
}
